import { z } from 'zod';
import {
  CATEGORY_MAX,
  CATEGORY_MIN,
  CATEGORY_REFUSAL,
} from '../../src/domain/catalog/catalog';
import { IdSchema, MoneySchema, RowVersionSchema, StageSchema } from './common.schema';

/**
 * A heading on the price list, and any word the lab uses for one.
 *
 * It was a closed enum of the five the lab started with, which made a sixth
 * impossible to record: the client asked for category creation on the Items
 * screen and there was nowhere for a new name to go. Bounded rather than
 * closed, because a category is a word on an item and the lab knows its own
 * words better than this file does.
 */
const CategorySchema = z
  .string()
  .trim()
  .min(CATEGORY_MIN, CATEGORY_REFUSAL.tooShort)
  .max(CATEGORY_MAX, CATEGORY_REFUSAL.tooLong);

/** One kit per tooth, or one kit for the whole span. The server decides, not the client. */
const KindSchema = z.enum(['unit', 'span']);

const ItemNameSchema = z.string().trim().min(1, 'Give the item a name').max(160);
const PriceSchema = z.number().nonnegative('A price cannot be below zero');

/**
 * `GET /catalog`, the price list. Section 2.5 of the system test handover.
 *
 * `id` is what a case keeps, so a rename reaches every case that carries the
 * item. Retired items are listed too, `active: false`, so an old case can still
 * show its item's name; intake offers only the active ones. Prices are omitted
 * for a caller without `pay`, `admin` or `pricing`, section 7.10 item 2.
 */
export const CatalogItemSchema = z.object({
  id: IdSchema,
  name: z.string(),
  category: CategorySchema,
  unitPrice: MoneySchema.nullish(),
  kind: KindSchema,
  active: z.boolean(),
  rowVersion: RowVersionSchema,
});

export const CatalogSchema = z.object({
  items: z.array(CatalogItemSchema),
  categories: z.array(z.string()),
});

/** `POST /catalog/items`. Requires `pricing`. 409 on a name already on the list. */
export const NewCatalogItemSchema = z.object({
  name: ItemNameSchema,
  category: CategorySchema,
  unitPrice: PriceSchema,
  kind: KindSchema,
});

/**
 * `PATCH /catalog/items/{id}`, any of the three. Requires `pricing` and
 * If-Match. Retiring is `active: false`; nothing is ever deleted, because an
 * old case and its invoice have to keep their item. A price change applies to
 * new cases only: each kit keeps `unitPriceAtOrder`.
 */
export const UpdateCatalogItemSchema = z.object({
  name: ItemNameSchema.optional(),
  /**
   * Moving an item to another heading, which is also how a category is renamed
   * and how one is emptied: there is no category row to edit, so the items are
   * the only handle.
   */
  category: CategorySchema.optional(),
  unitPrice: PriceSchema.optional(),
  active: z.boolean().optional(),
});

/**
 * `GET /stages`. The key and the label, and nothing else: the capacity this
 * carried was a number nobody set and nothing checked, and the lab asked for
 * it gone.
 */
export const StageConfigSchema = z.object({
  key: StageSchema,
  label: z.string(),
});

export const StageConfigListSchema = z.object({
  items: z.array(StageConfigSchema),
});

export type CatalogItemPayload = z.infer<typeof CatalogItemSchema>;
